import { APIPaths, InfluencerTicketApi } from "@unit/apis";
import { useAtom } from "jotai";
import { useCallback, useMemo } from "react";

import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import { API_URL } from "@/global-state/firebase-settings";
import { idTokenAtom, loadingAtom } from "@/global-state/jotai-atom";

export const useInfluencerTicketApi = () => {
  const { setAppSnackbar } = useAppSnackbar();
  const [, setLoading] = useAtom(loadingAtom);
  const [idToken] = useAtom(idTokenAtom);
  const influencerTicketApi = useMemo(() => new InfluencerTicketApi(API_URL, idToken), [idToken]);

  const fetchInfluencerTicketList = useCallback(
    async (parameters: APIPaths["/app/influencer-ticket/list"]["get"]["parameters"]["query"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.getInfluencerTicketList(parameters);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const fetchInfluencerTicket = useCallback(
    async (id: APIPaths["/app/influencer-ticket/{id}"]["get"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.getInfluencerTicketById(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const offerRejectionInfluencerTicket = useCallback(
    async (id: APIPaths["/app/influencer-ticket/{id}/offer-rejection"]["put"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.putInfluencerTicketOfferRejectionById(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const offerApprovalInfluencerTicket = useCallback(
    async (id: APIPaths["/app/influencer-ticket/{id}/offer-approval"]["put"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.putInfluencerTicketOfferApprovalById(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const setUseInfluencerTicket = useCallback(
    async (id: APIPaths["/app/influencer-ticket/{id}/use"]["put"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.putInfluencerTicketUseById(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );
  const scheduleInfluencerTicket = useCallback(
    async (
      id: APIPaths["/app/influencer-ticket/{id}/schedule"]["put"]["parameters"]["path"]["id"],
      requestBody: APIPaths["/app/influencer-ticket/{id}/schedule"]["put"]["requestBody"]["content"]["application/json"],
    ) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.putInfluencerTicketScheduleById(id, requestBody);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const PRPostInfluencerTicket = useCallback(
    async (
      id: APIPaths["/app/influencer-ticket/{id}/posts"]["put"]["parameters"]["path"]["id"],
      requestBody: APIPaths["/app/influencer-ticket/{id}/posts"]["put"]["requestBody"]["content"]["application/json"],
    ) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.putInfluencerTicketPRPostById(id, requestBody);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );
  const ratingInfluencerTicket = useCallback(
    async (
      id: APIPaths["/app/influencer-ticket/{id}/rating"]["put"]["parameters"]["path"]["id"],
      requestBody: APIPaths["/app/influencer-ticket/{id}/rating"]["put"]["requestBody"]["content"]["application/json"],
    ) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.putInfluencerTicketRatingById(id, requestBody);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const reportChatRoom = useCallback(
    async (
      id: APIPaths["/app/influencer-ticket/{id}/rating"]["put"]["parameters"]["path"]["id"],
      requestBody: APIPaths["/app/influencer-ticket/{id}/chat-report/new"]["post"]["requestBody"]["content"]["application/json"],
    ) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.postInfluencerTicketChatReportById(id, requestBody);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const fetchReportChatList = useCallback(
    async (id: APIPaths["/app/influencer-ticket/{id}/chat-report/list-all"]["get"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.getInfluencerTicketChatReportList(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const postInfluencerTicketNotifyChatMessage = useCallback(
    async (
      id: APIPaths["/app/influencer-ticket/{id}/notify-chat-message"]["post"]["parameters"]["path"]["id"],
      requestBody: APIPaths["/app/influencer-ticket/{id}/notify-chat-message"]["post"]["requestBody"]["content"]["application/json"],
    ) => {
      return await influencerTicketApi.postInfluencerTicketNotifyChatMessage(id, requestBody);
    },
    [influencerTicketApi],
  );

  const getInfluencerTicketStatusLogList = useCallback(
    async (id: APIPaths["/app/influencer-ticket/{id}/status-log/list-all"]["get"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.getInfluencerTicketStatusLogList(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );

  const putInfluencerTicketUseCancelById = useCallback(
    async (id: APIPaths["/app/influencer-ticket/{id}/use-cancel"]["put"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.putInfluencerTicketUseCancelById(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );
  const putInfluencerTicketRequestCancelById = useCallback(
    async (id: APIPaths["/app/influencer-ticket/{id}/request-cancel"]["put"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await influencerTicketApi.putInfluencerTicketRequestCancelById(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [influencerTicketApi],
  );
  return {
    fetchInfluencerTicketList,
    fetchInfluencerTicket,
    offerRejectionInfluencerTicket,
    offerApprovalInfluencerTicket,
    setUseInfluencerTicket,
    scheduleInfluencerTicket,
    PRPostInfluencerTicket,
    ratingInfluencerTicket,
    reportChatRoom,
    fetchReportChatList,
    postInfluencerTicketNotifyChatMessage,
    getInfluencerTicketStatusLogList,
    putInfluencerTicketRequestCancelById,
    putInfluencerTicketUseCancelById,
  };
};
